import MaskedTextField from '@/common/components/MaskedTextField'
import TextField from '@/common/components/TextField'
import { cpfMask, phoneMask } from '@/common/constants/masks.constant'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import { ReviewButtons } from '@/features/establishmentLeadsFunnel/components/ReviewButtons'
import { ContactData, ContactState } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import ResendLeadContactEmailModal from "../../../common/components/ResendLeadContactEmailModal"
import type { PublicCompanyLeadsFunnelUpdateContactValidationSchema } from "../schemas/update.schema"

interface ContactPersonalInformationSectionProps {
  publicCompanyLeadId?: string;
  contact?: ContactData
  showReviewButtons?: boolean
  isPending?: boolean
  approveContact: UseMutateFunction<any, Error, { description?: string } | undefined, unknown>
  declineContact: UseMutateFunction<any, Error, { description?: string } | undefined, unknown>
  disableFields?: boolean
}

export const ContactPersonalInformationSection = ({
  contact,
  publicCompanyLeadId,
  showReviewButtons,
  isPending,
  approveContact,
  declineContact,
  disableFields,
}: ContactPersonalInformationSectionProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateContactValidationSchema>()

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex justify-between">
        <p className="text-lg font-bold">Dados pessoais</p>

        {showReviewButtons && (
          <ReviewButtons
            reviewState={contact?.last_review?.state}
            onApprove={approveContact}
            onDecline={declineContact}
            isPending={isPending}
          />
        )}
      </div>
      {contact?.last_review?.state === 'disapproved' && (
        <RequestedReview description={contact?.last_review.description} />
      )}
      <div className="w-full flex gap-2">
        <TextField
          label="Nome"
          className="w-2/3"
          defaultValue={contact?.full_name}
          errorMessage={errors?.contacts?.full_name?.message || undefined}
          {...register('contacts.full_name')}
          disabled={disableFields}
          markAsRequired
        />
        <TextField
          label="Cargo"
          maxLength={32}
          className="w-1/3"
          defaultValue={contact?.role_in_company}
          errorMessage={errors?.contacts?.role_in_company?.message || undefined}
          {...register('contacts.role_in_company')}
          disabled={disableFields}
          markAsRequired
        />
      </div>

      <div className="w-full flex gap-2">
        <MaskedTextField
          label="CPF"
          mask={cpfMask}
          className="w-2/3"
          defaultValue={contact?.cpf}
          errorMessage={errors?.contacts?.cpf?.message || undefined}
          {...register("contacts.cpf")}
          disabled={disableFields}
          markAsRequired
        />
        <MaskedTextField
          label="Telefone"
          mask={phoneMask}
          className="w-1/3"
          defaultValue={contact?.phone?.number}
          errorMessage={errors?.contacts?.phone?.number?.message || undefined}
          {...register("contacts.phone.number")}
          disabled={disableFields}
          markAsRequired
        />
      </div>

      <div className="flex gap-4 items-center">
        <TextField
          label="Email"
          className="w-full"
          value={contact?.email}
          errorMessage={errors?.contacts?.email?.message || undefined}
          {...register('contacts.email')}
          disabled={disableFields}
          markAsRequired
        />

        {contact?.state === ContactState.awaiting_client_documents && (
          <ResendLeadContactEmailModal contact={contact} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })} />
        )}
      </div>

    </div>
  )
}
